// The "how to read this" card: six short sections over the run, each with a picture
// drawn from the page's own palette and the same hatch the timeline paints, so what
// the reader sees in the guide is what is on the rows behind it.
//
// It opens by itself the first time a run is opened on this browser, and after that
// only when asked: the toolbar's ? button, the header link, the ? key. Anything that
// opens it carries the class `guide-open`, so the click that opened it is not read as
// a click outside.

import { el } from "./dom.js";
import { unitRGB, UNIT_ABOUT } from "./isa.js";

const SEEN = "playground.guide.seen";
let panel = null;
let armed = false;

export function guideIsOpen() { return !!panel; }

export function openGuide() {
  if (panel) return;
  panel = build();
  document.body.appendChild(panel);
  panel.querySelector(".guide-close").focus();
  document.addEventListener("pointerdown", onDown, true);
  seen(true);
}

export function closeGuide() {
  if (!panel) return;
  document.removeEventListener("pointerdown", onDown, true);
  panel.remove();
  panel = null;
}

export function toggleGuide() { panel ? closeGuide() : openGuide(); }

// The first run opened on this browser explains itself; every later one does not.
// Called once the run is drawn, so the card never lands on a loading page.
export function offerGuide() {
  if (!seen()) openGuide();
}

// The ? key, from anywhere in a run. Installed when the first run opens, so the
// landing page has no guide to open.
export function armGuide() {
  if (armed) return;
  armed = true;
  document.addEventListener("keydown", onKey, true);
}

function seen(set) {
  try {
    if (set) { localStorage.setItem(SEEN, "1"); return true; }
    return localStorage.getItem(SEEN) === "1";
  } catch (_) { return set ? true : false; }     // a private window: show it, once
}

function onKey(e) {
  if (e.key === "Escape" && panel) { closeGuide(); e.stopPropagation(); return; }
  if (e.key !== "?") return;
  const t = e.target;
  if (t && (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA")) return;
  toggleGuide();
  e.preventDefault();
  e.stopPropagation();
}

function onDown(e) {
  if (!panel) return;
  if (panel.contains(e.target) || (e.target.closest && e.target.closest(".guide-open"))) return;
  closeGuide();
}

// --- the card ---------------------------------------------------------------------

function build() {
  const root = el("div", "guide");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-label", "How to read this");
  const head = el("div", "guide-head");
  head.appendChild(el("span", "strong", "How to read this"));
  const x = el("button", "guide-close", "✕");
  x.title = "close (Esc)";
  x.onclick = () => closeGuide();
  head.appendChild(x);
  root.appendChild(head);
  for (const make of [secSlice, secRows, secColours, secWaiting, secZoom, secKeys]) root.appendChild(make());
  root.appendChild(el("div", "guide-foot dim", "Press ? for this card again."));
  return root;
}

function section(title, figure, ...paras) {
  const s = el("section", "guide-sec");
  s.appendChild(el("h3", null, title));
  if (figure) s.appendChild(figure);
  for (const p of paras) s.appendChild(typeof p === "string" ? el("p", null, p) : p);
  return s;
}

function secSlice() {
  return section("The slice", figSlice(),
    "Each box is a chip. The strips in it are its cores — TensorCores TC0 and TC1, SparseCores SC0–3 — and its host, drawn along time from left to right; the colour is what the core was doing.",
    "Drag across a strip to pick the window the timeline shows. Double-click a chip to open it.");
}

function secRows() {
  return section("The rows", figRows(),
    "The timeline says the same thing row by row: a summary row for the chip, its host, one row per TensorCore bundle stream, and one per SparseCore with its 16 tiles in the lane below.",
    "The chevron opens a row into sub-rows: the unit operations it issued, and the DMAs it started.");
}

function secColours() {
  // A three-column grid: the swatch, the unit's name, what it is. The cells are the
  // grid's own children, so the names line up down the card.
  const key = el("div", "guide-units");
  for (const [u, what] of UNIT_ABOUT) {
    const sw = el("i", "swatch");
    sw.style.background = unitRGB(u);
    key.appendChild(sw);
    key.appendChild(el("b", null, u));
    key.appendChild(el("span", "dim", what));
  }
  return section("The colours", figBars(),
    "A bar's height is the share of that slice of time the core was issuing; its colour is the unit that did most of the work. Grey is empty bundles — the padding the compiler left for a unit's latency.",
    key);
}

function secWaiting() {
  return section("Waiting", figWaits(),
    "Hatched red is a core parked on a sync flag, waiting for another core or a DMA to set it; the label is the flag and how long the wait lasted. A grey floor is the core idle for the host.",
    "A DMA is a thin bar from issue to start and a block for the transfer, outlined when it crosses to another chip. Teal in a SparseCore's lane means its tiles were running.");
}

function secZoom() {
  return section("Zooming in", figZoom(),
    "Ctrl+wheel zooms. Past a few cycles per pixel the density bar gives way to one box per bundle, labelled with its pc; click one for its instructions.",
    "Clicking anything fills the detail panel below, which names who released a wait and links into the chip page's listing.");
}

const KEYS = [
  ["ctrl+wheel", "zoom · drag pans"],
  ["shift+drag", "zoom to that range (dragging on the time axis does too)"],
  ["← →", "previous / next item on the selected row: bundle, wait, DMA, flag, host command (nothing selected: pan)"],
  ["shift+← →", "pan"],
  ["+ −", "zoom"],
  ["f", "fit the cores' activity"],
  ["n  p", "next / previous busy stretch"],
  ["z", "zoom to the selection"],
  ["[  ]", "previous / next wait on the selected row"],
  ["s", "walk the worst stalls"],
  ["1  2", "the unit and DMA sub-rows"],
  ["Esc", "clear the selection"],
  ["?", "this card"],
];
const CHIP_KEYS = [
  ["← →", "step a bundle"],
  ["↑ ↓", "walk the listing"],
  ["space", "play the cursor forward"],
  ["Backspace", "back to the slice"],
];

function secKeys() {
  const s = el("section", "guide-sec");
  s.appendChild(el("h3", null, "Getting around"));
  const grid = el("div", "guide-keys");
  for (const [k, what] of KEYS) { grid.appendChild(el("kbd", null, k)); grid.appendChild(el("span", null, what)); }
  s.appendChild(grid);
  s.appendChild(el("p", "dim guide-sub", "On the chip page"));
  const g2 = el("div", "guide-keys");
  for (const [k, what] of CHIP_KEYS) { g2.appendChild(el("kbd", null, k)); g2.appendChild(el("span", null, what)); }
  s.appendChild(g2);
  return s;
}

// --- the pictures -------------------------------------------------------------------

const FIG_W = 508;

function fig(w, h, draw) {
  const c = el("canvas", "guide-fig");
  const dpr = window.devicePixelRatio || 1;
  c.width = Math.round(w * dpr); c.height = Math.round(h * dpr);
  c.style.width = w + "px"; c.style.height = h + "px";
  const g = c.getContext("2d");
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.textBaseline = "middle";
  draw(g, w, h);
  return c;
}

const SANS = (px) => `${px}px ui-sans-serif, system-ui`;
const MONO = (px) => `${px}px ui-monospace, monospace`;

// The timeline's wait hatch (timeline.js makeHatch), so a picture of a wait is the
// same eight pixels as the row it stands for.
function hatch(g) {
  const p = document.createElement("canvas");
  p.width = 8; p.height = 8;
  const q = p.getContext("2d"), c = unitRGB("wait");
  q.fillStyle = c; q.globalAlpha = 0.35; q.fillRect(0, 0, 8, 8); q.globalAlpha = 1;
  q.strokeStyle = c; q.lineWidth = 2;
  q.beginPath(); q.moveTo(-2, 10); q.lineTo(10, -2); q.moveTo(-2, 2); q.lineTo(2, -2); q.moveTo(6, 10); q.lineTo(10, 6); q.stroke();
  return g.createPattern(p, "repeat");
}

// A band painted the way paintDensity paints a row: a column every few pixels, its
// height the share of the time issuing, its colour the unit that led, with the wait
// and the empty bundles stacked over it.
function band(g, x, y, w, h, cols, cw = 3) {
  for (let i = 0; i * cw < w; i++) {
    const c = cols[i % cols.length];
    if (!c) continue;
    const [u, busy, wait = 0, nop = 0] = c;
    // A short pattern repeated reads as a barcode; this slow jitter keeps a busy band
    // looking like a trace. A pattern of one column is a statement about that one
    // height ("issuing every cycle"), and is left alone.
    const j = cols.length > 1 ? 0.7 + 0.3 * Math.abs(0.7 * Math.sin(i * 0.23) + 0.3 * Math.sin(i * 0.61)) : 1;
    const cx = x + i * cw, cwid = Math.min(cw, x + w - cx);
    let yy = y + h;
    const put = (frac, color, alpha) => {
      const hh = Math.round(frac * h);
      if (hh <= 0) return;
      g.globalAlpha = alpha == null ? 1 : alpha;
      g.fillStyle = color;
      g.fillRect(cx, yy - hh, cwid, hh);
      g.globalAlpha = 1;
      yy -= hh;
    };
    put(busy * j, unitRGB(u));
    put(wait, unitRGB("wait"), 0.82);
    put(nop, unitRGB("nop"));
  }
}

// Made-up but plausible mixes, so every picture looks like a row of a real run.
const BUSY = [["mxu", .9], ["mxu", 1], ["valu", .6], ["vld", .5], ["mxu", .95], ["xlu", .7], ["valu", .45], ["eup", .6], ["mxu", .85], ["vst", .5], ["mxu", 1], ["valu", .35, 0, .25]];
const MIXED = [["valu", .5], ["vld", .4], ["mxu", .7], ["scalar", .3], ["valu", .55], ["mxu", .8], ["sync", .25, .5], ["vst", .45], ["mxu", .6, .2], ["valu", .4]];
const PARKED = [["valu", .3, .55], ["mxu", .2, .7], [null], ["scalar", .15, .7], ["valu", .25, .6]];
const IDLEISH = [null, null, ["alu", .25], null, null, null, null, ["stream", .2], null, null];
const SCBUSY = [["stream", .7], ["alu", .5], ["vld", .6], ["stream", .8], ["alu", .4], ["vst", .55], ["stream", .75], ["alu", .45]];

// A chip as the slice map draws it: a title and one strip per core, plus the host.
const STRIPS = [["TC0", BUSY], ["TC1", MIXED], ["SC0", SCBUSY], ["SC1", PARKED], ["SC2", IDLEISH], ["SC3", IDLEISH], ["host", null]];
const STRIP_H = 11, STRIP_GAP = 3, CARD_TOP = 20;
const CARD_H = CARD_TOP + STRIPS.length * (STRIP_H + STRIP_GAP) + 3;

function figSlice() {
  return fig(FIG_W, CARD_H + 8, (g) => {
    card(g, 4, 4, 224, "chip 0 (0,0)", true);
    card(g, 260, 4, 224, "chip 1 (0,1)", false);
  });

  function card(g, x, y, w, title, brushed) {
    g.fillStyle = unitRGB("bg"); g.fillRect(x, y, w, CARD_H);
    g.strokeStyle = unitRGB("line"); g.strokeRect(x + .5, y + .5, w - 1, CARD_H - 1);
    g.font = SANS(10); g.fillStyle = unitRGB("fg"); g.fillText(title, x + 8, y + 11);
    const sx = x + 34, sw = w - 42;
    STRIPS.forEach(([name, cols], i) => {
      const sy = y + CARD_TOP + i * (STRIP_H + STRIP_GAP);
      g.font = SANS(9); g.fillStyle = unitRGB(i < 2 ? "fg" : "dim");
      g.fillText(name, x + 6, sy + STRIP_H / 2);
      g.fillStyle = unitRGB("idle"); g.fillRect(sx, sy, sw, STRIP_H);
      if (cols) band(g, sx, sy, sw, STRIP_H, cols);
      else {                                  // the host: a sparse tick per command
        g.fillStyle = unitRGB("accent");
        for (const f of [.02, .06, .3, .32, .55, .8, .95]) g.fillRect(sx + f * sw, sy + 3, 2, 6);
      }
    });
    if (!brushed) return;
    // A window dragged on a strip, the way the map hands one to the timeline.
    const a = sx + sw * .34, b = sx + sw * .62, top = y + CARD_TOP - 2, bot = y + CARD_H - 3;
    g.fillStyle = unitRGB("accent"); g.globalAlpha = .18;
    g.fillRect(a, top, b - a, bot - top); g.globalAlpha = 1;
    g.fillStyle = unitRGB("sel");
    g.fillRect(a - 1, top - 2, 2, bot - top + 4); g.fillRect(b - 1, top - 2, 2, bot - top + 4);
  }
}

function figRows() {
  const rows = [
    { label: "chip 0 (0,0)", kind: "chip", h: 20, chev: "▾" },
    { label: "host", kind: "host", h: 16, depth: 1 },
    { label: "TC0", kind: "tc", h: 22, depth: 1, chev: "▾" },
    { label: "units", kind: "units", h: 15, depth: 2 },
    { label: "dma", kind: "dma", h: 15, depth: 2 },
    { label: "SC0", kind: "sc", h: 28, depth: 1, chev: "▸", badge: "16 tiles" },
  ];
  const H = rows.reduce((a, r) => a + r.h, 0);
  return fig(FIG_W, H, (g, w) => {
    const G = 118;
    g.fillStyle = unitRGB("panel"); g.fillRect(0, 0, G, H);
    let y = 0;
    for (const r of rows) {
      if (r.kind === "chip") { g.fillStyle = unitRGB("hi"); g.fillRect(0, y, G, r.h); }
      g.strokeStyle = unitRGB("line"); g.beginPath(); g.moveTo(0, y + r.h - .5); g.lineTo(w, y + r.h - .5); g.stroke();
      g.font = SANS(r.kind === "chip" ? 11 : 10);
      g.fillStyle = unitRGB(r.depth === 2 ? "dim" : r.kind === "host" ? "dim" : "fg");
      let lx = 6 + (r.depth || 0) * 8;
      if (r.chev) { g.fillText(r.chev, lx, y + r.h / 2); lx += 12; }
      g.fillText(r.label, lx, y + r.h / 2);
      if (r.badge) {
        const bw = g.measureText(r.badge).width + 8;
        g.strokeStyle = unitRGB("line"); g.strokeRect(G - bw - 6.5, y + r.h / 2 - 7.5, bw, 14);
        g.fillStyle = unitRGB("dim"); g.font = SANS(9); g.fillText(r.badge, G - bw - 2, y + r.h / 2);
      }
      paint(g, r, G, y, w - G, r.h);
      y += r.h;
    }
    g.strokeStyle = unitRGB("line"); g.beginPath(); g.moveTo(G - .5, 0); g.lineTo(G - .5, H); g.stroke();
  });

  function paint(g, r, x, y, w, h) {
    if (r.kind === "chip") return band(g, x, y + 2, w, h - 4, BUSY);
    if (r.kind === "host") {
      g.fillStyle = unitRGB("accent");
      for (const f of [.02, .05, .28, .3, .52, .78, .93]) g.fillRect(x + f * w, y + 5, 2, 6);
      return;
    }
    if (r.kind === "tc") {
      band(g, x, y + 2, w, h - 4, MIXED);
      g.fillStyle = hatch(g); g.fillRect(x + w * .62, y + 2, w * .16, h - 4);
      return;
    }
    if (r.kind === "units") {
      for (let i = 0; i < 16; i++) { g.fillStyle = unitRGB(i % 3 ? "mxu" : "xlu"); g.fillRect(x + 8 + i * 22, y + 4, 9, h - 8); }
      return;
    }
    if (r.kind === "dma") {
      for (const [a, b] of [[.05, .22], [.35, .48], [.66, .9]]) {
        g.fillStyle = unitRGB("dma"); g.globalAlpha = .35; g.fillRect(x + a * w - 14, y + h / 2 - 2, 14, 4); g.globalAlpha = 1;
        g.fillRect(x + a * w, y + 3, (b - a) * w, h - 6);
      }
      return;
    }
    // A SparseCore: the sequencer above, its tiles in the lane below.
    const th = Math.round(h * .42);
    band(g, x, y + 2, w, h - th - 4, SCBUSY);
    g.fillStyle = unitRGB("panel"); g.fillRect(x, y + h - th, w, th);
    tiles(g, x, y + h - th, w, th);
  }
}

// The tile lane: a column per slice of time, teal, its height the tiles' mean work.
function tiles(g, x, y, w, h, label = "16/16 tiles") {
  g.strokeStyle = unitRGB("line"); g.beginPath(); g.moveTo(x, y + .5); g.lineTo(x + w, y + .5); g.stroke();
  g.fillStyle = unitRGB("sc");
  for (let i = 0; i < w; i++) {
    const f = i / w;
    if (f < .12 || (f > .42 && f < .52) || f > .86) continue;
    const hh = Math.max(2, Math.round((h - 3) * (.45 + .4 * Math.abs(Math.sin(i / 7)))));
    g.globalAlpha = .55 + .45 * Math.abs(Math.sin(i / 31));
    g.fillRect(x + i, y + h - 1 - hh, 1, hh);
  }
  g.globalAlpha = 1;
  g.fillStyle = unitRGB("fg"); g.font = SANS(9); g.textBaseline = "top";
  g.fillText(label, x + w * .14, y + 2);
  g.textBaseline = "middle";
}

function figBars() {
  return fig(FIG_W, 56, (g, w) => {
    const cell = Math.floor(w / 3), bw = cell - 16;
    const cases = [
      [[["mxu", 1]], "issuing every cycle"],
      [[["valu", .45]], "issuing half the time"],
      [[["misc", 0, 0, .5]], "empty bundles"],
    ];
    cases.forEach(([cols, text], i) => {
      const x = i * cell + 8;
      g.fillStyle = unitRGB("idle"); g.fillRect(x, 2, bw, 34);
      band(g, x, 2, bw, 34, cols);
      g.strokeStyle = unitRGB("line"); g.strokeRect(x + .5, 2.5, bw - 1, 33);
      g.fillStyle = unitRGB("dim"); g.font = SANS(10);
      g.fillText(text, x, 47);
    });
  });
}

function figWaits() {
  const lanes = [
    ["waiting on a sync flag", (g, x, y, w, h) => {
      band(g, x, y, w * .12, h, MIXED);
      g.fillStyle = hatch(g); g.fillRect(x + w * .12, y + 2, w * .88 - 2, h - 4);
      g.fillStyle = unitRGB("fg"); g.font = SANS(10);
      g.fillText("flag 60  7.1 us", x + w * .12 + 8, y + h / 2);
    }],
    ["idle for the host", (g, x, y, w, h) => {
      const fh = 8;
      g.fillStyle = unitRGB("host"); g.globalAlpha = .25; g.fillRect(x, y + h - 2 - fh, w - 2, fh); g.globalAlpha = 1;
      g.strokeStyle = unitRGB("host"); g.strokeRect(x + .5, y + h - 2.5 - fh, w - 3, fh);
      g.fillStyle = unitRGB("dim"); g.font = SANS(10);
      g.fillText("host · flag 5", x + 5, y + h - 2 - fh / 2);
    }],
    ["a DMA: issue, then the transfer", (g, x, y, w, h) => {
      g.fillStyle = unitRGB("dma"); g.globalAlpha = .35; g.fillRect(x, y + h / 2 - 2, w * .12, 4); g.globalAlpha = 1;
      g.fillRect(x + w * .12, y + 3, w * .3, h - 6);
      g.fillRect(x + w * .5, y + 3, w * .34, h - 6);
      g.strokeStyle = unitRGB("cross"); g.strokeRect(x + w * .5 + .5, y + 3.5, w * .34 - 1, h - 7);
    }],
    ["a SparseCore's tiles, running", (g, x, y, w, h) => {
      g.fillStyle = unitRGB("panel"); g.fillRect(x, y, w, h);
      tiles(g, x, y, w, h, "12/16 tiles");
    }],
  ];
  return fig(FIG_W, lanes.length * 26, (g) => {
    // The lanes are as wide as the captions leave them, so no caption can run off the
    // picture whatever the reader's font measures. The cross-chip outline is in the
    // prose under the figure rather than in a caption that would not fit.
    g.font = SANS(11);
    let cap = 0;
    for (const [text] of lanes) cap = Math.max(cap, g.measureText(text).width);
    const lw = Math.max(150, Math.min(236, FIG_W - 14 - Math.ceil(cap)));
    lanes.forEach(([text, draw], i) => {
      const y = i * 26 + 2;
      g.fillStyle = unitRGB("idle"); g.fillRect(0, y, lw, 20);
      draw(g, 0, y, lw, 20);
      g.fillStyle = unitRGB("dim"); g.font = SANS(11);
      g.fillText(text, lw + 12, y + 10);
    });
  });
}

function figZoom() {
  return fig(FIG_W, 62, (g, w) => {
    const half = Math.floor(w / 2) - 10;
    g.fillStyle = unitRGB("idle"); g.fillRect(0, 14, half, 30);
    band(g, 0, 14, half, 30, BUSY);
    g.fillStyle = unitRGB("dim"); g.font = SANS(10);
    g.fillText("zoomed out: a bar per pixel", 0, 54);
    g.strokeStyle = unitRGB("line"); g.setLineDash([3, 3]);
    g.beginPath(); g.moveTo(half + 10.5, 8); g.lineTo(half + 10.5, 48); g.stroke();
    g.setLineDash([]);
    const x0 = half + 22, bw = 44;
    const boxes = [["mxu", 112], ["valu", 113], ["mxu", 114], ["vld", 115], ["mxu", 116]];
    boxes.forEach(([u, pc], i) => {
      const x = x0 + i * (bw + 2);
      if (x + bw > w) return;
      g.fillStyle = unitRGB(u); g.fillRect(x, 17, bw, 24);
      g.fillStyle = unitRGB("bg"); g.font = MONO(10);
      g.fillText(String(pc), x + 4, 29);
    });
    g.fillStyle = unitRGB("dim"); g.font = SANS(10);
    g.fillText("zoomed in: a box per bundle, with its pc", x0, 54);
  });
}
